import { useJsonForms } from "@jsonforms/react";
import { defaultTo } from "lodash";
import { Icon, TextInput, Tooltip } from "pepsico-ds";
import PropTypes from "prop-types";
import * as React from "react";
import { computeRequired } from "../shared/requiredWhen";
import "./text-input.scss";

const NAVIGATION_KEYS = [
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
];

const SHORTCUT_KEYS = ["c", "v", "a", "x"];

const isNumberOrInteger = (type) => type === "number" || type === "integer";

const isValidLength = (val, maxLength) => !maxLength || val.length <= maxLength;

const buildHelperTextLines = (helperText = "") => {
  const lineCounts = {};

  return helperText.split("\n").map((line) => {
    lineCounts[line] = (lineCounts[line] || 0) + 1;

    return {
      key: `${line}-${lineCounts[line]}`,
      text: line,
    };
  });
};

const TextInputField = ({
  value,
  updateValue,
  label,
  errors,
  schema,
  uiSchema,
  required,
  disabled,
  visible,
}) => {
  const isMac = /Macintosh|Mac OS X/.test(navigator.userAgent);

  const inputRef = React.useRef();
  const getUpdatedValue = (schema, val) => {
    if (schema.type === "number" || schema.type === "integer") {
      return val;
    }
    return val;
  };
  const ctx = useJsonForms();
  const jsonformState = ctx.core.data;
  const showValidation = ctx.config?.showValidation;
  const maxLength = uiSchema.maxLengthKey
    ? jsonformState[uiSchema.maxLengthKey]
    : uiSchema.maxLength;

  const [isFocused, setIsFocused] = React.useState(false);
  let errMsg =
    schema.message && errors.indexOf("must match pattern") > -1
      ? schema.message
      : errors;
  errMsg = errors.includes("multiple of") ? schema.message : errMsg;
  if (errors.includes("must NOT be valid") && schema.not) {
    errMsg = "Value not allowed";
  }
  if (
    errors.includes("must NOT have fewer than 1 characters") &&
    schema.minLength === 1
  ) {
    errMsg = "This field is required";
  }

  const parseValue = (val) => {
    let parsedVal = parseFloat(val);
    if (parsedVal < 0 || Number.isNaN(parsedVal)) {
      parsedVal = "";
    }
    return parsedVal;
  };

  const handleNumberOrIntegerChange = (val, schema, uiSchema) => {
    if (!isValidLength(val, uiSchema.maxLength)) {
      return null;
    }
    return parseValue(val);
  };

  const onChange = (val) => {
    if (isNumberOrInteger(schema.type)) {
      val = handleNumberOrIntegerChange(val, schema, uiSchema);
      if (val === null) {
        return;
      }
    }
    if (schema.type === "string" && uiSchema.uppercase) {
      val = val.toUpperCase();
    }
    const updatedValue = getUpdatedValue(schema, val);
    updateValue(updatedValue);
  };

  const isAllowedShortcut = (e) => {
    const isShortcutPressed = isMac ? e.metaKey : e.ctrlKey;
    return isShortcutPressed && SHORTCUT_KEYS.includes(e.key);
  };

  const isAllowedKey = (key) => NAVIGATION_KEYS.includes(key);

  const shouldHandleIncludedChars = () =>
    !!uiSchema.includedChars?.key && !!jsonformState[uiSchema.includedChars.key];

  const isValidCharacter = (char, validCharacters) =>
    validCharacters.includes(char) || char === "Backspace";

  const handleNumberOrIntegerKeyDown = (e) => {
    if (!/^\d*$/.test(e.key) && !isAllowedKey(e.key) && !e.ctrlKey) {
      e.preventDefault();
    }
  };

  const handleIncludedChars = (e) => {
    const validCharacters = jsonformState[uiSchema.includedChars.key];
    const isUnique = uiSchema.includedChars.isUnique;

    // Check for duplicates if unique
    const isCharacterAlreadyPresent = value.includes(e.key);
    if (isUnique && isCharacterAlreadyPresent) {
      e.preventDefault();
      return;
    }

    // Validate against valid characters
    if (!isValidCharacter(e.key, validCharacters)) {
      e.preventDefault();
    }
  };

  const onKeyDown = (e) => {
    if (isAllowedShortcut(e) || isAllowedKey(e.key)) {
      return;
    }

    if (isNumberOrInteger(schema.type)) {
      handleNumberOrIntegerKeyDown(e);
    }

    if (shouldHandleIncludedChars()) {
      handleIncludedChars(e);
    }
  };

  const onPaste = (e) => {
    if (isNumberOrInteger(schema.type)) {
      const paste = (e.clipboardData || window.clipboardData).getData("text");
      const filteredPaste = paste.replace(/\D/g, ""); // Remove non-digit characters
      e.preventDefault();
      onChange(filteredPaste);
    }
  };

  const isNumericSchemaType = isNumberOrInteger(schema.type);
  const helperTextLines = uiSchema?.newLine
    ? buildHelperTextLines(uiSchema?.helperText)
    : [];
  const minValue = uiSchema.min ?? (isNumericSchemaType ? 0 : undefined);

  const diableButton =
    disabled || uiSchema.readOnly || isNumericSchemaType ? "disable-button" : "";
  // Display info icon with info text
  if (inputRef.current && uiSchema?.info) {
    const infoIcon =
      inputRef.current?.parentElement?.previousSibling?.getElementsByClassName(
        "material-icons-outlined"
      );
    if (infoIcon?.length && infoIcon?.[0]) {
      infoIcon[0].title = uiSchema.info;
    }
  }

  return visible ? (
    <>
      <div className="input-text-wrapper">
        <TextInput
          required={computeRequired(required, uiSchema, jsonformState)}
          className={`${uiSchema?.className ?? ""} ${diableButton} inputType-${schema?.type === "integer" ? "number" : schema?.type}`}
          disabled={disabled || uiSchema.readOnly || uiSchema.disabled}
          maxLength={maxLength ?? ""}
          onKeyDown={onKeyDown}
          type={schema?.type === "integer" ? "number" : schema?.type || "text"}
          error={
            (isFocused || showValidation) && !disabled && errMsg ? errMsg : undefined
          }
          label={label}
          info={uiSchema?.info || ""}
          showLabelIcon={!!uiSchema?.info}
          ref={inputRef}
          onUpdate={onChange}
          onPaste={onPaste}
          onBlur={() => {
            let trimmedValue = value ?? "";
            if (schema.type === "string") {
              trimmedValue = trimmedValue.trim();
              if (trimmedValue === "") {
                trimmedValue = undefined;
              }
            }
            updateValue(trimmedValue);
            !isFocused && setIsFocused(true);
          }}
          value={value === 0 ? "0" : defaultTo(value, undefined)}
          placeholderText={uiSchema.placeholder ?? ""}
          readOnly={uiSchema.readonly}
          helperText={
            uiSchema?.newLine ? (
              <div className="helper-text-container">
                {helperTextLines.map((line) => (
                  <span key={line.key}>{line.text}</span>
                ))}
              </div>
            ) : (
              uiSchema?.helperText
            )
          }
          min={minValue}
          step={uiSchema.step}
          max={uiSchema.max}
        />
        {uiSchema?.tooltipText && uiSchema?.tooltipText?.length > 0 && (
          <div className="tooltip-text">
            <Tooltip text={uiSchema.tooltipText}>
              <Icon alt="" icon="info" size="small" />
            </Tooltip>
          </div>
        )}
      </div>
      {uiSchema.helpText && (
        <div className="input-help-text">
          <Tooltip text={uiSchema.helpText} className="help-tooltip">
            <Icon icon="info" size="small" />
          </Tooltip>
          Help
        </div>
      )}
    </>
  ) : null;
};

TextInputField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updateValue: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  errors: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  schema: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string, // Fixed 'schema.message' validation
  }).isRequired,
  uiSchema: PropTypes.shape({
    readOnly: PropTypes.bool,
    readonly: PropTypes.bool,
    disabled: PropTypes.bool,
    maxLength: PropTypes.number,
    maxLengthKey: PropTypes.string,
    className: PropTypes.string,
    info: PropTypes.string,
    placeholder: PropTypes.string,
    uppercase: PropTypes.bool,
    newLine: PropTypes.bool,
    helperText: PropTypes.string,
    helpText: PropTypes.string,
    tooltipText: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    includedChars: PropTypes.shape({
      key: PropTypes.string,
      isUnique: PropTypes.bool,
    }),
  }),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  visible: PropTypes.bool.isRequired,
};

TextInputField.defaultProps = {
  value: "",
  errors: "",
  required: false,
  disabled: false,
  uiSchema: {},
};

export default TextInputField;
